import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SlideLoader } from '~/components/common/SkeletonLoaders';

import { Link, useNavigate } from 'react-router-dom';
import { api_getBannersByBannerGroupName } from '~/backend/api_calls';

import { convertImageUrl_L_to_F } from '~/backend/helper';
import useWindowSize from '~/components/hooks/useWindowSize';

const HeroSlider = () => {
	const { t } = useTranslation();

	const [autoplay, setAutoplay] = useState(
		Autoplay({ delay: 5000, stopOnInteraction: false, stopOnMouseEnter: false }, emblaRoot => emblaRoot.parentElement)
	);
	const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [autoplay]);
	const [loading, setLoading] = useState(true);
	const navigate = useNavigate();
	const [banners, setBanners] = useState([]);
	const [bannerControlsLabels, setBannerControlsLabels] = useState({ prev: '', next: '' });
	const groupName = 'HeroSlider';
	const { width } = useWindowSize();

	const onSelect = useCallback(() => {
		if (!emblaApi) {
			return;
		}
		//console.log(emblaApi.selectedScrollSnap())
		handleSetBannerControlLabels(emblaApi.selectedScrollSnap());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [emblaApi]);

	const scrollPrev = useCallback(() => {
		if (emblaApi) emblaApi.scrollPrev();
		autoplay.reset();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [emblaApi]);

	const scrollNext = useCallback(() => {
		if (emblaApi) emblaApi.scrollNext();
		autoplay.reset();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [emblaApi]);

	const handleSetBannerControlLabels = currentBannerKey => {
		const bannersLength = banners.length;
		if (bannersLength < 2) return;

		if (currentBannerKey === 0) {
			setBannerControlsLabels({ prev: banners[bannersLength - 1].tl, next: banners[currentBannerKey + 1].tl });
			return;
		}

		if (currentBannerKey + 1 === bannersLength) {
			setBannerControlsLabels({ prev: banners[currentBannerKey - 1].tl, next: banners[0].tl });
			return;
		}

		setBannerControlsLabels({ prev: banners[currentBannerKey - 1].tl, next: banners[currentBannerKey + 1].tl });
		return;
	};

	// Runs once after init rendering
	useEffect(() => {
		setLoading(true);

		api_getBannersByBannerGroupName(groupName)
			.then(json => {
				setBanners(json.items);
				const containsVideoBanner = json.items.find(x => x.video_url);
				setAutoplay(
					Autoplay(
						{ delay: containsVideoBanner ? 350000 : 5000, stopOnInteraction: false, stopOnMouseEnter: false },
						emblaRoot => emblaRoot.parentElement
					)
				);

				setLoading(false);
			})
			.catch(error => {
				setLoading(false);
				console.log(error);
			});
	}, []);

	useEffect(() => {
		if (!emblaApi) {
			return;
		}

		onSelect();
		emblaApi.on('select', onSelect);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [emblaApi]);

	if (loading) return <SlideLoader />;

	if (banners.length === 0) return <canvas width="1440" height="805" id="slider_placeholder" />;

	const renderBannerContent = banner => {
		// Video has priority
		if (banner.video_url) {
			let videoUrl = banner.video_url;
			if (width <= 480) {
				videoUrl = banner.video_url_mobile ?? banner.video_url;
			}
			return (
				<div className="home-video">
					<iframe
						src={
							'https://www.youtube.com/embed/' +
							videoUrl +
							`?rel=0&version=3&autoplay=1&modestbranding=1&autohide=1&controls=0&showinfo=0&playlist=${videoUrl}&loop=1&mute=1&disablekb=1&fs=0`
						}
						width={1920}
						height={1080}
						frameBorder="0"
						allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
						allowFullScreen
						title={banner.tl}
					/>
					<Link to={banner.nu} target={banner.ut} className="banner-cta see-more">
						{banner.rct && banner.rct !== '' ? banner.rct : 'Show More'}
					</Link>
				</div>
			);
		}

		// Otherwise render image
		if (banner.nu) {
			return (
				<Link to={banner.nu} className="viewall-button banner-item" key={banner.id} href={navigate}>
					<picture>
						<source
							media="(max-width:480px)"
							src={convertImageUrl_L_to_F(banner.img2) ?? banner.img1}
							srcSet={convertImageUrl_L_to_F(banner.img2) ?? banner.img}
						/>
						<img src={banner.img1} alt={banner.tl} />
						{/* NESTED LINK?? */}
					</picture>
				</Link>
			);
		} else {
			return (
				<picture>
					<source
						media="(max-width:480px)"
						src={convertImageUrl_L_to_F(banner.img2) ?? banner.img1}
						srcSet={convertImageUrl_L_to_F(banner.img2) ?? banner.img}
					/>
					<img src={banner.img1} alt={banner.tl} />
				</picture>
			);
		}
	};

	return (
		<>
			<div className="embla homepage-slider">
				<div className="embla__viewport" ref={emblaRef}>
					<div className="embla__container">
						{banners.map(item => (
							<div className="embla__slide" key={item.id}>
								{renderBannerContent(item)}
							</div>
						))}
					</div>
				</div>
				{banners.length > 1 && emblaApi ? (
					<div className="embla_controls">
						<button className="embla_control embla__prev" onClick={scrollPrev} title="Previous">
							<svg xmlns="http://www.w3.org/2000/svg" width="7.095" height="12.69" viewBox="0 0 7.095 12.69">
								<path
									id="Path_38025"
									data-name="Path 38025"
									d="M18,7.5l5.815,5.815L18,19.129"
									transform="translate(-17.47 -6.97)"
									fill="none"
									stroke="#000"
									strokeLinejoin="round"
									strokeWidth="1.5"
								/>
							</svg>
							<div className="text">{bannerControlsLabels.prev}</div>
						</button>

						<button className="embla_control embla__next" onClick={scrollNext} title="Next">
							<div className="text">{bannerControlsLabels.next}</div>
							<svg xmlns="http://www.w3.org/2000/svg" width="7.095" height="12.69" viewBox="0 0 7.095 12.69">
								<path
									id="Path_38025"
									data-name="Path 38025"
									d="M18,7.5l5.815,5.815L18,19.129"
									transform="translate(-17.47 -6.97)"
									fill="none"
									stroke="#000"
									strokeLinejoin="round"
									strokeWidth="1.5"
								/>
							</svg>
						</button>
					</div>
				) : null}
			</div>
		</>
	);
};

export default HeroSlider;
